/**
 * Single source of truth (Option A): the questionnaire-builder chat edits the
 * PERSISTED draft, which equals what the builder renders only if the on-screen
 * edits are saved first. Edits are never saved behind the user's back: with a
 * dirty workspace the guard asks (`confirmSave`, "Save and continue") and saves
 * only on a yes. If the user declines, or the save fails, it does NOT proceed:
 * it returns `{ ok: false }` and invokes onCancelled / onSaveFailure so the
 * caller can keep the user's input or roll back an optimistic selection.
 *
 * Nothing on the panel holds a turn back: the server's applier and readiness
 * are the one verdict on the draft, and a chat turn is how a blank asset card
 * gets fixed or removed (QA4 FE3).
 */
export interface ChatSendWorkspace<TDraft> {
  currentDraft: TDraft;
  isDirty: boolean;
  saveChanges: () => Promise<{ draft?: TDraft | null } | null | undefined | void>;
}

export interface ResolveDraftForChatSendOptions {
  onSaveFailure: () => void;
  /**
   * Ask before saving unsaved edits. Resolves true to save and continue.
   * Omitted only by callers that already asked.
   */
  confirmSave?: () => Promise<boolean>;
  /** The user chose to keep editing instead of saving. */
  onCancelled?: () => void;
}

/**
 * Returns `{ ok: true, draft }` when the chat may proceed (the draft can be null
 * on a FRESH study that hasn't generated yet — that's valid, the planner then
 * generates it). Returns `{ ok: false }` ONLY when the user kept editing or a
 * dirty save genuinely failed, so callers must branch on `ok`, not on the draft
 * being truthy — otherwise a fresh (null-draft) study would be wrongly treated
 * as a failure.
 */
export async function resolveDraftForChatSend<TDraft>(
  workspace: ChatSendWorkspace<TDraft>,
  onSaveFailureOrOptions: (() => void) | ResolveDraftForChatSendOptions,
): Promise<{ ok: true; draft: TDraft } | { ok: false }> {
  const options =
    typeof onSaveFailureOrOptions === "function"
      ? { onSaveFailure: onSaveFailureOrOptions }
      : onSaveFailureOrOptions;

  if (!workspace.isDirty) {
    return { ok: true, draft: workspace.currentDraft };
  }
  if (options.confirmSave && !(await options.confirmSave())) {
    options.onCancelled?.();
    return { ok: false };
  }
  const saveResult = await workspace.saveChanges();
  if (!saveResult?.draft) {
    options.onSaveFailure();
    return { ok: false };
  }
  return { ok: true, draft: saveResult.draft };
}
